import { fmtDateTime } from '../lib/dates.ts';
import type { CoverageReport } from '../lib/api.ts';

/**
 * The headline report, rendered so the answer is the first thing read.
 *
 * Sites that have NOT opened are listed first and marked, because that
 * is the actionable half. A report that buries the six silent sites
 * under eighteen green ticks makes the reader do the work.
 */
export const Coverage = ({ report }: { readonly report: CoverageReport }) => {
  const missing = report.sites.filter((s) => s.openedAt === null);
  const seen = report.sites.filter((s) => s.openedAt !== null);
  const pct = Math.round((report.opened / Math.max(report.sentTo, 1)) * 100);

  const time = (iso: string): string =>
    fmtDateTime(iso);

  return (
    <section className="cov">
      <p className="covBody">{report.body}</p>

      <p>
        <a
          className="dlLink"
          href={`/reports/coverage/${report.broadcastId}.csv`}
          download
        >
          Download CSV
        </a>
      </p>

      <div className="covStats">
        <span>
          <b>{report.opened}</b> of <b>{report.sentTo}</b> sites opened
          <i> ({pct}%)</i>
        </span>
        {report.requiresAck && (
          <span>
            <b>{report.acked}</b> acknowledged
          </span>
        )}
        <span className="mono">{time(report.sentAt)}</span>
      </div>

      <div className="covBar" role="img"
        aria-label={`${pct} percent of sites opened`}>
        <span style={{ width: `${pct}%` }} />
      </div>

      {missing.length > 0 && (
        <>
          <p className="mono covHead">Not opened &mdash; {missing.length}</p>
          <ul className="covList">
            {missing.map((s) => (
              <li key={s.siteId} className="covMiss">
                <span>{s.siteName}</span>
                <span className="mono">no receipt</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {seen.length > 0 && (
        <>
          <p className="mono covHead">Opened &mdash; {seen.length}</p>
          <ul className="covList">
            {seen.map((s) => (
              <li key={s.siteId}>
                <span>{s.siteName}</span>
                <span className="covWhen">
                  {s.openedOn && <em>{s.openedOn}</em>}
                  {s.openedAt && ` ${time(s.openedAt)}`}
                  {s.ackedBy && (
                    <b className="covAck">ack {s.ackedBy}</b>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
};
